import { AdjustmentsService, MetadataService, PortfolioService, TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import {
    AssessmentConfiguration,
    AssessmentMetadata, ConfigurationDescription, CreateTcoAssessment, Portfolio, TcoAssessment, TcoAssessmentInput, TcoAssessmentSummaryOutput
} from '@amzn/awscat-portfolio-assessment-types';

import { makeName } from '../../common/arn';
import { constants } from '../../common/Localization';
import { deepCopy } from '../../common/util';
import { defaultConfiguration } from './EmptyAssumptions';

export const defaultTcoAssessmentId = 'New Assessment';

export const generateNewTcoAssessment = (portfolioId: string, portfolioName: string): TcoAssessment => {
    const portfolio = makeName('portfolio', portfolioId);
    const input: TcoAssessmentInput = {
        awsApplication: makeName('portfolio-application', portfolioId),
        awsApplicationServer: makeName('portfolio-application-server', portfolioId),
        awsDatabase: makeName('portfolio-database', portfolioId),
        awsDatabaseApplication: makeName('portfolio-database-application', portfolioId),
        awsNetwork: makeName('portfolio-additional-network', portfolioId),
        awsServer: makeName('portfolio', portfolioId),
        awsStorage: makeName('portfolio-additional-storage', portfolioId),
        migration: {
            portfolio,
        },
        onPremises: {},
        portfolioName: portfolioName,
    };
    const tcoAssessment: TcoAssessment = {
        configuration: defaultConfiguration,
        createdAt: new Date(),
        createdBy: '',
        id: defaultTcoAssessmentId,
        input,
        updatedAt: new Date(),
        updatedBy: '',
    };

    return tcoAssessment;
};

export const createTCOAssessment = async (
    portfolioId: string, portfolioName: string, tcoService: TcoService, tcoAssessment?: Partial<TcoAssessment>,
    copyConfigurationAssessmentId = '',
): Promise<TcoAssessment> => {
    const portfolio = makeName('portfolio', portfolioId);
    const { configuration, input } = generateNewTcoAssessment(portfolioId, portfolioName);
    const newTcoAssessment: CreateTcoAssessment = { configuration: tcoAssessment?.configuration ?? configuration,
        input: tcoAssessment?.input ?? input };
    const assessment = await tcoService.create(newTcoAssessment, portfolio, copyConfigurationAssessmentId);
    return assessment;
};

export const updatePortfolioWithTcoAssessment = async (
    portfolio: Portfolio, assessmentId: string, portfolioService: PortfolioService
): Promise<Portfolio> => {
    const portfolioToUpdate = {...portfolio};
    if (!portfolioToUpdate.defaultTcoAssessmentId) {
        portfolioToUpdate.defaultTcoAssessmentId = assessmentId;
    }
    if (!portfolioToUpdate.tcoAssessmentIds) {
        portfolioToUpdate.tcoAssessmentIds = [];
    }
    portfolioToUpdate.tcoAssessmentIds.unshift(assessmentId);
    return await portfolioService.update(portfolioToUpdate);
};

export const deleteAssessmentFromPortfolio = async (
    portfolio: Portfolio, assessmentId: string, portfolioService: PortfolioService, adjustmentsService: AdjustmentsService
): Promise<Portfolio> => {
    const portfolioToUpdate = {...portfolio};
    if (portfolioToUpdate.tcoAssessmentIds) {
        const indexToDelete = portfolioToUpdate.tcoAssessmentIds.findIndex((tcoAssessmentId => tcoAssessmentId === assessmentId));
        if (indexToDelete >= 0) {
            portfolioToUpdate.tcoAssessmentIds.splice(indexToDelete, 1);
            // if an assessment is deleted, remove corresponding adjustments
            await adjustmentsService.deleteAdjustmentsByPortfolioIdTcoAssessmentId(portfolio.id, assessmentId);
        }
    }
    if (portfolioToUpdate.defaultTcoAssessmentId === assessmentId) {
        portfolioToUpdate.defaultTcoAssessmentId = portfolioToUpdate.tcoAssessmentIds?.[0];
    }
    return await portfolioService.update(portfolioToUpdate);
};

export const updateAssessmentName = async (
    assessmentId: string, name: string, metadataService: MetadataService
): Promise<AssessmentMetadata> => {
    const metadata = await metadataService.get(assessmentId);
    if (metadata) {
        metadata.name = name;
    }
    return await metadataService.update(assessmentId, metadata);
};

export const updateTCOAssessment = async (
    portfolioId: string, assessment: TcoAssessment, tcoService: TcoService, copyConfigurationAssessmentId = '',
): Promise<TcoAssessment> => {
    const portfolio = makeName('portfolio', portfolioId);
    // TODO: Add PageActionLoadingService
    // TODO: Add FlagService
    return await tcoService.save(assessment, portfolio, copyConfigurationAssessmentId);
};

export const loadDescription = async (tcoService: TcoService, region = '') => {
    const description: ConfigurationDescription = await tcoService.getDescription(region);
    return description;
};

export const MIN_ASSESSMENTS_PER_PORTFOLIO = 1;
export const MAX_ASSESSMENTS_PER_PORTFOLIO = 4;

const tcoSummaryNameMap: Map<string, string> = new Map([
    ['server', 'Compute'],
    ['dedicatedHost', 'Dedicated Host'],
    ['database', 'Database'],
    ['databaseLicense', 'Database licenses - 3rd party'],
    ['serverMaintenance', 'Server maintenance'],
    ['infrastructure', 'Server rack/hardware'],
    ['facilities', 'Server facilities'],
    ['software', 'Server software'],
    ['storage', 'Storage'],
    ['storageRack', 'Storage rack/hardware'],
    ['storageFacilities', 'Storage facilities'],
    ['storageSoftware', 'Storage software'],
    ['network', 'Network'],
    ['labor', 'IT labor'],
    ['support', 'Support'],
    ['total', 'Total'],
    ['manualAdjustment', 'Manual adjustment'],
    ['adjustedTotal', 'Adjusted total'],
    ['vmware', 'VMware Cloud on AWS'],
    ['vendorSoftware', 'OS licenses - 3rd party'],
    ['other', 'Other costs'],
]);

export const tcoSummaryUserFriendly = (input: string) => {
    return tcoSummaryNameMap.get(input) ?? input;
};

export const wrapStrong = (input: JSX.Element | string, strong = false) => {
    return strong ? (<strong>{input}</strong>) : input;
};

export const getYearsObject = ({ summary }: TcoAssessmentSummaryOutput): Record<string, string> => {
    const years: Record<string, string> = {};
    summary.cashFlow.aws.yearly.total.forEach((year, index) => {
        const yearText: string = `${constants.YEAR} ` + (index + 1);
        years[yearText] = '';
    });
    return years;
};
/**
 * Merge in default assumptions if not present
 * @param  {AssessmentConfiguration} configuration
 */
export const addDefaultConfigurations = (configuration: AssessmentConfiguration) => Object.keys(configuration || {}).length <= 2 ?
    Object.assign(deepCopy(defaultConfiguration), configuration) : 
    configuration;
